(function () {
  'use strict';

  var POLL_INTERVAL_MS = 60000;
  var SLIDE_SELECTOR = '.mmm-announcement-bar__slide';
  var VISIBLE_SLIDE_SELECTOR = '.mmm-announcement-bar__slide:not([hidden])';

  function isValidDateString(value) {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return false;
    }

    var parts = value.split('-').map(Number);
    var year = parts[0];
    var month = parts[1];
    var day = parts[2];
    var date = new Date(year, month - 1, day);

    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  }

  function getShopDateString(timeZone) {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date());
  }

  function addCalendarDays(dateStr, days) {
    var parts = dateStr.split('-').map(Number);
    var date = new Date(parts[0], parts[1] - 1, parts[2] + days);
    var year = date.getFullYear();
    var month = String(date.getMonth() + 1).padStart(2, '0');
    var day = String(date.getDate()).padStart(2, '0');

    return year + '-' + month + '-' + day;
  }

  function isBlockExpired(slide, shopTimezone) {
    if (slide.dataset.autoHide !== 'true') {
      return false;
    }

    var start = (slide.dataset.startDate || '').trim();
    var displayDays = parseInt(slide.dataset.displayDays, 10) || 7;

    if (!isValidDateString(start)) {
      return false;
    }

    var hideDate = addCalendarDays(start, displayDays);
    var today = getShopDateString(shopTimezone);

    return today >= hideDate;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class MmmAnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.flickity = null;
      this.pollTimer = null;
    }

    connectedCallback() {
      this.slider = this.querySelector('.mmm-announcement-bar__slider');
      this.bar = this.querySelector('.mmm-announcement-bar');

      if (!this.slider) {
        return;
      }

      this._initWhenReady();
      this._bindEditorEvents();
    }

    disconnectedCallback() {
      this.destroy();
      this._unbindEditorEvents();
    }

    _initWhenReady(attempts) {
      var self = this;
      attempts = attempts === undefined ? 50 : attempts;

      if (window.theme && window.theme.Slideshow) {
        this.init();
        return;
      }

      if (attempts <= 0) {
        return;
      }

      requestAnimationFrame(function () {
        self._initWhenReady(attempts - 1);
      });
    }

    init() {
      this.destroyPoll();
      this.evaluateAutoHide();
      this.refresh();

      if (this.dataset.designMode !== 'true') {
        this.pollTimer = window.setInterval(
          this._onPollTick.bind(this),
          POLL_INTERVAL_MS
        );
      }
    }

    destroy() {
      this.destroyCarousel();
      this.destroyPoll();
    }

    destroyPoll() {
      if (this.pollTimer) {
        window.clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    }

    _onPollTick() {
      var hadExpired = this.evaluateAutoHide();
      if (hadExpired) {
        this.refresh();
      }
    }

    evaluateAutoHide() {
      if (this.dataset.designMode === 'true') {
        return false;
      }

      var shopTimezone = this.dataset.shopTimezone || 'America/New_York';
      var changed = false;
      var slides = this.querySelectorAll(SLIDE_SELECTOR);

      slides.forEach(function (slide) {
        if (slide.hasAttribute('data-manually-hidden')) {
          return;
        }

        var shouldHide = isBlockExpired(slide, shopTimezone);

        if (shouldHide && !slide.hidden) {
          slide.hidden = true;
          changed = true;
        }
      });

      return changed;
    }

    getVisibleSlides() {
      return Array.prototype.filter.call(
        this.querySelectorAll(SLIDE_SELECTOR),
        function (slide) {
          return !slide.hidden;
        }
      );
    }

    refresh() {
      var self = this;

      this.destroyCarousel();
      this.evaluateAutoHide();

      var visibleSlides = this.getVisibleSlides();

      if (visibleSlides.length === 0) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this._syncBarColors(visibleSlides[0]);

      if (visibleSlides.length === 1 || this.dataset.carousel !== 'true') {
        var firstVisible = visibleSlides[0];
        this.querySelectorAll(SLIDE_SELECTOR).forEach(function (slide) {
          slide.hidden = slide !== firstVisible;
        });
        this._syncBarColors(firstVisible);
        return;
      }

      this.querySelectorAll(SLIDE_SELECTOR).forEach(function (slide) {
        if (!slide.hasAttribute('data-manually-hidden') && !isBlockExpired(slide, self.dataset.shopTimezone || 'America/New_York')) {
          slide.hidden = false;
        }
      });

      this.initCarousel();
    }

    initCarousel() {
      var self = this;
      var visibleSlides = this.getVisibleSlides();

      if (visibleSlides.length <= 1) {
        return;
      }

      var speed = parseInt(this.dataset.autoplay, 10) || 5000;
      var autoPlay =
        !prefersReducedMotion() && this.dataset.carousel === 'true'
          ? speed
          : false;

      var args = {
        autoPlay: autoPlay,
        avoidReflow: true,
        cellAlign: window.theme.config && window.theme.config.rtl ? 'right' : 'left',
        cellSelector: VISIBLE_SLIDE_SELECTOR,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        callbacks: {
          onChange: function (index) {
            var slides = self.getVisibleSlides();
            self._syncBarColors(slides[index]);
          },
          onInit: function (currentSlide) {
            self._syncBarColors(currentSlide);
          }
        }
      };

      this.flickity = new window.theme.Slideshow(this.slider, args);
    }

    destroyCarousel() {

      if (this.flickity && typeof this.flickity.destroy === 'function') {
        this.flickity.destroy();
      }

      this.flickity = null;
    }

    _syncBarColors(slide) {
      if (!slide || !this.bar) {
        return;
      }

      var styles = getComputedStyle(slide);
      var bg = styles.getPropertyValue('--mmm-announcement-bg').trim();
      var color = styles.getPropertyValue('--mmm-announcement-color').trim();

      if (bg) {
        this.bar.style.setProperty('--mmm-announcement-bg', bg);
      }

      if (color) {
        this.bar.style.setProperty('--mmm-announcement-color', color);
      }
    }

    hideBlock(blockId) {
      var slide = this.querySelector('[data-block-id="' + blockId + '"]');

      if (!slide) {
        return;
      }

      slide.setAttribute('data-manually-hidden', '');
      slide.hidden = true;
      this.refresh();
    }

    showBlock(blockId) {
      var slide = this.querySelector('[data-block-id="' + blockId + '"]');

      if (!slide) {
        return;
      }

      var shopTimezone = this.dataset.shopTimezone || 'America/New_York';

      if (isBlockExpired(slide, shopTimezone)) {
        return;
      }

      slide.removeAttribute('data-manually-hidden');
      slide.hidden = false;
      this.refresh();
    }

    onBlockSelect(blockId) {
      if (!this.flickity) {
        return;
      }

      var slide = this.querySelector('[data-block-id="' + blockId + '"]');

      if (!slide) {
        return;
      }

      var visibleSlides = this.getVisibleSlides();
      var index = visibleSlides.indexOf(slide);

      if (index === -1) {
        return;
      }

      this.flickity.goToSlide(index);
      this.flickity.pause();
    }

    onBlockDeselect() {
      if (this.flickity && typeof this.flickity.play === 'function') {
        this.flickity.play();
      }
    }

    _bindEditorEvents() {
      if (!window.Shopify || !Shopify.designMode) {
        return;
      }

      this._onBlockSelect = this._handleBlockSelect.bind(this);
      this._onBlockDeselect = this._handleBlockDeselect.bind(this);
      this._onSectionUnload = this._handleSectionUnload.bind(this);

      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
      document.addEventListener('shopify:section:unload', this._onSectionUnload);
    }

    _unbindEditorEvents() {
      if (!this._onBlockSelect) {
        return;
      }

      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
      document.removeEventListener('shopify:section:unload', this._onSectionUnload);
    }

    _handleBlockSelect(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) {
        return;
      }

      this.onBlockSelect(event.detail.blockId);
    }

    _handleBlockDeselect(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) {
        return;
      }

      this.onBlockDeselect();
    }

    _handleSectionUnload(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) {
        return;
      }

      this.destroy();
    }
  }

  if (!customElements.get('mmm-announcement-bar')) {
    customElements.define('mmm-announcement-bar', MmmAnnouncementBar);
  }
})();
